import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStore } from 'src/stores';
import { AccordionHeader } from './AccordionHeader';
import useStyles from './billDetailsStyles';
import { formatDate } from './billDetailsUtils';
import { ConfirmActionModal } from './ConfirmActionModal';
import { describeChange, describeEdit } from './billEditHistoryUtils';

/** One recorded change, as "what it was" beside "what it is now" rather than instead of it. */
function ChangeRow(_ref) {
  let change = _ref.change;
  const classes = useStyles();
  const _describeChange = describeChange(change),
    from = _describeChange.from,
    to = _describeChange.to;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.editHistoryChange
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.editHistoryChangeLabel
  }, change.label), /*#__PURE__*/React.createElement("span", {
    className: classes.editHistoryValues
  }, from === null ? null : /*#__PURE__*/React.createElement("span", {
    className: classes.editHistoryOldValue
  }, from), to === null ? null : /*#__PURE__*/React.createElement("span", {
    className: classes.editHistoryNewValue
  }, to)));
}

/** Every field one edit moved. Defined once so the two places that show them cannot drift. */
function ChangeRows(_ref2) {
  let entry = _ref2.entry;
  return (entry.changes || []).map(change => /*#__PURE__*/React.createElement(ChangeRow, {
    change: change,
    key: "".concat(entry.id, "-").concat(change.path)
  }));
}

/**
 * An older entry's body: the changes and nothing else.
 *
 * It sits under an accordion header that already says who and when, so repeating that here states
 * the same thing twice about the same edit.
 */
function ChangeList(_ref3) {
  let entry = _ref3.entry;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.editHistoryEntry
  }, /*#__PURE__*/React.createElement(ChangeRows, {
    entry: entry
  }));
}

/** The latest edit, which is shown outright rather than behind a header of its own. */
function LatestEdit(_ref4) {
  let entry = _ref4.entry;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.editHistoryEntry
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editHistorySummary
  }, describeEdit(entry), /*#__PURE__*/React.createElement("span", {
    className: classes.editHistoryWhen
  }, formatDate(entry.editedAt))), /*#__PURE__*/React.createElement(ChangeRows, {
    entry: entry
  }));
}

/**
 * Who changed what on this bill, and the way back to the original (LB-5840).
 *
 * The latest edit is shown outright because it is the one that explains what is on screen; the rest
 * are fetched only when somebody asks for them. The first read takes a single entry, which is all
 * the collapsed view renders — `total` comes back with it, so the card knows whether there is more
 * to offer without having read it.
 *
 * The editor's name is sent denormalised on the row rather than resolved here. The comparable GWD
 * panel resolves user ids through an endpoint gated on `ManageTeamMembers`, so it shows "N/A" for
 * exactly the role most likely to be editing bills — and a name stored with the edit stays the name
 * as it was at the time.
 */
export const BillEditHistoryCard = observer(_ref5 => {
  var _bill$editHistory$tot, _bill$editHistory, _ref6, _bill$latestEdit, _bill$editHistory2, _bill$editHistory$ent, _bill$editHistory3;
  let bill = _ref5.bill,
    canRestore = _ref5.canRestore,
    onRestore = _ref5.onRestore;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isShowingAll = _useState2[0],
    setIsShowingAll = _useState2[1];
  const _useState3 = useState(() => new Set()),
    _useState4 = _slicedToArray(_useState3, 2),
    openIds = _useState4[0],
    setOpenIds = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isConfirmingRestore = _useState6[0],
    setIsConfirmingRestore = _useState6[1];
  const billId = bill.billId;
  useEffect(() => {
    // One entry and a count: everything the collapsed card renders, and nothing it does not.
    store.uploadedBills.getBillEditHistoryAsync.run({
      billId,
      take: 1
    });
    setIsShowingAll(false);
    setOpenIds(new Set());
  }, [billId, store.uploadedBills]);
  const total = (_bill$editHistory$tot = (_bill$editHistory = bill.editHistory) === null || _bill$editHistory === void 0 ? void 0 : _bill$editHistory.total) !== null && _bill$editHistory$tot !== void 0 ? _bill$editHistory$tot : 0;
  const latest = (_ref6 = (_bill$latestEdit = bill.latestEdit) !== null && _bill$latestEdit !== void 0 ? _bill$latestEdit : (_bill$editHistory2 = bill.editHistory) === null || _bill$editHistory2 === void 0 || (_bill$editHistory2 = _bill$editHistory2.entries) === null || _bill$editHistory2 === void 0 ? void 0 : _bill$editHistory2[0]) !== null && _ref6 !== void 0 ? _ref6 : null;

  // Nothing has ever been changed on this bill, so there is nothing to say about it.
  if (!latest || total === 0) return null;
  const older = isShowingAll ? ((_bill$editHistory$ent = (_bill$editHistory3 = bill.editHistory) === null || _bill$editHistory3 === void 0 ? void 0 : _bill$editHistory3.entries) !== null && _bill$editHistory$ent !== void 0 ? _bill$editHistory$ent : []).filter(entry => entry.id !== latest.id) : [];
  const handleShowAll = async () => {
    // No `take`: the service caps the page itself, and `skip` keeps anything past that reachable.
    const resp = await store.uploadedBills.getBillEditHistoryAsync.run({
      billId
    });

    // Only on success. Expanding regardless would hide the control and render no older entries —
    // the trail still holds just the latest — leaving a dead end with nothing to retry from. This
    // read is silent, so a failure says nothing of its own either.
    if (resp !== null && resp !== void 0 && resp.isSuccessful) setIsShowingAll(true);
  };
  const handleRestore = async () => {
    setIsConfirmingRestore(false);
    await onRestore();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.card
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, "This bill was edited")), canRestore ? /*#__PURE__*/React.createElement(Button, {
    onClick: () => setIsConfirmingRestore(true),
    size: "small",
    variant: "outlined"
  }, "Restore original") : null), /*#__PURE__*/React.createElement(LatestEdit, {
    entry: latest
  }), older.map(entry => {
    const isExpanded = openIds.has(entry.id);
    return /*#__PURE__*/React.createElement("div", {
      key: entry.id
    }, /*#__PURE__*/React.createElement(AccordionHeader, {
      isExpanded: isExpanded,
      onToggle: () => setOpenIds(previous => {
        const next = new Set(previous);
        if (!next.delete(entry.id)) next.add(entry.id);
        return next;
      }),
      title: "".concat(describeEdit(entry), " \xB7 ").concat(formatDate(entry.editedAt))
    }), isExpanded ? /*#__PURE__*/React.createElement(ChangeList, {
      entry: entry
    }) : null);
  }), !isShowingAll && total > 1 ? /*#__PURE__*/React.createElement("button", {
    className: classes.editHistoryMore,
    onClick: handleShowAll,
    type: "button"
  }, "Show all ".concat(total, " edits")) : null, /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Restore original",
    isOpen: isConfirmingRestore,
    message: "Every change made to this bill will be thrown away and it will go back to what was read from the document. This cannot be undone.",
    onClose: () => setIsConfirmingRestore(false),
    onConfirm: handleRestore,
    title: "Restore the original bill?"
  }));
});